/**
 * Composable per Customers autocomplete con loading e error handling centralizzati
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import type { BaseAutocompleteOptions } from '@/lib/autocompletes';
import type { CustomerAutocompleteResult } from '@/lib/models/customer';
import { getCustomersAutocomplete } from '@/lib/autocompletes/customers';

export function useCustomersAutocomplete(options: BaseAutocompleteComposableOptions = {}) {
	const customers = ref<CustomerAutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	// Usa withLoading iniettato dal parent, fallback su istanza locale se non fornito
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchCustomers(options: BaseAutocompleteOptions = {}): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getCustomersAutocomplete(buildOdataQuery, options);
				if (token.isLatest()) {
					customers.value = result;
				}
			} catch (err) {
				console.error('❌ ERROR useCustomersAutocomplete.searchCustomers:', err);
				error('Errore durante il caricamento dei Clienti! Ricaricare la pagina e riprovare.');
				if (token.isLatest()) {
					customers.value = [];
				}
			}
		}, 'searchCustomers');
	}

	function clearCustomers(): void {
		customers.value = [];
	}

	return {
		customers: readonly(customers),
		searchCustomers,
		clearCustomers,
	};
}
